// Funções puras do Painel Global de Conexões — sem JSX, sem side-effects,
// sem `api`/`i18n` importados diretamente (o que precisa de tradução recebe
// `translate` injetado pelo chamador) — testáveis sem depender de
// renderização de componente (o frontend não tem infraestrutura de teste de
// componentes configurada, mesmo padrão já usado no módulo de Tarefas).

// Mesmo conjunto de valores aceitos pelo backend
// (ListAllCompaniesConnectionsService.ts) — filtro nunca envia um valor
// fora desses.
export const KNOWN_STATUSES = ["CONNECTED", "OPENING", "DISCONNECTED", "PENDING", "qrcode"];
export const KNOWN_CHANNELS = ["whatsapp", "whatsapp_oficial", "facebook", "instagram"];
export const SEARCH_PARAM_MAX_LENGTH = 100;

const isNonEmptyString = value => typeof value === "string" && value.trim() !== "";

const isPositiveIntegerId = value => {
  if (typeof value !== "number" && typeof value !== "string") return false;
  if (typeof value === "string" && value.trim() === "") return false;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0;
};

// Monta os params de GET /super/connections explicitamente — nunca envia
// campo vazio ou fora do conjunto conhecido. pageNumber sempre enviado.
// searchParam é sempre cortado no mesmo limite do backend (100
// caracteres) — nunca dispara uma consulta que o backend rejeitaria.
export const buildConnectionsFilterParams = ({
  companyId,
  status,
  channel,
  searchParam,
  pageNumber
} = {}) => {
  const params = {
    pageNumber: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1
  };

  if (isPositiveIntegerId(companyId)) params.companyId = companyId;
  if (isNonEmptyString(status) && KNOWN_STATUSES.includes(status)) params.status = status;
  if (isNonEmptyString(channel) && KNOWN_CHANNELS.includes(channel)) params.channel = channel;

  if (isNonEmptyString(searchParam)) {
    const trimmed = searchParam.trim().slice(0, SEARCH_PARAM_MAX_LENGTH);
    if (trimmed !== "") params.searchParam = trimmed;
  }

  return params;
};

// health -> par {background,color} fixo — mesmo padrão já usado em
// TaskChips.js (comprovadamente legível em tema claro e escuro, já que os
// dois valores da cor vêm sempre juntos, nunca dependendo da cor de texto
// padrão do tema).
const HEALTH_STYLES = {
  healthy: { background: "#2e7d32", color: "#fff" },
  connecting: { background: "#1976d2", color: "#fff" },
  awaiting_scan: { background: "#ef6c00", color: "#fff" },
  alert: { background: "#c62828", color: "#fff" },
  unmonitored: { background: "#757575", color: "#fff" }
};
const DEFAULT_HEALTH_STYLE = { background: "#757575", color: "#fff" };
const KNOWN_HEALTHS = ["healthy", "connecting", "awaiting_scan", "alert", "unmonitored"];

export const getHealthStyle = health => HEALTH_STYLES[health] || DEFAULT_HEALTH_STYLE;

// Nunca cai em "healthy" por omissão — um valor de saúde desconhecido (ex.:
// backend novo com um estado que o frontend ainda não conhece) usa a chave
// de tradução de "alerta".
export const getHealthLabelKey = health =>
  KNOWN_HEALTHS.includes(health)
    ? `superConnections.health.${health}`
    : "superConnections.health.alert";

// `translate` é injetado pelo chamador (i18n.t) — nunca importado aqui.
export const formatApproxTime = (seconds, translate) => {
  const t = typeof translate === "function" ? translate : key => key;

  if (typeof seconds !== "number" || Number.isNaN(seconds) || seconds < 0) {
    return t("superConnections.time.unknown");
  }
  if (seconds < 60) return t("superConnections.time.lessThanMinute");

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t("superConnections.time.minutes", { count: minutes });

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("superConnections.time.hours", { count: hours });

  const days = Math.floor(hours / 24);
  return t("superConnections.time.days", { count: days });
};

// Formata `updatedAt` (string ISO vinda da API) para exibição — nunca
// lança exceção com valor ausente/inválido, sempre volta um texto seguro.
export const formatConnectionUpdatedAt = value => {
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  const pad = n => String(n).padStart(2, "0");
  return (
    `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()} ` +
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
};
